/**
 * Invisible spam protection for the public forms.
 *
 * Two signals, both checked server-side in `functions/lib/validation.js`:
 *
 *   website    a honeypot input. It is hidden from people (and from screen
 *              readers and the tab order), so only a bot filling in every field
 *              it finds will ever set it.
 *   elapsedMs  how long the form was open. A submission completed in under two
 *              seconds was not typed by a human. This is sent as a duration
 *              rather than a start timestamp on purpose: both readings come
 *              from the same clock, so skew between the visitor's device and
 *              the server cannot distort it.
 *
 * Chosen over a CAPTCHA deliberately: no third-party script, no cookie banner
 * implications, no friction for a lab manager trying to send an enquiry.
 */

import { useCallback, useRef, useState } from "react";

export function HoneypotField({ value, onChange }) {
  return (
    <div aria-hidden="true" className="absolute w-px h-px -left-[9999px] overflow-hidden">
      <label htmlFor="website-url">Leave this field empty</label>
      <input
        id="website-url"
        name="website"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

export function useFormGuard() {
  const startedAt = useRef(Date.now());
  const [website, setWebsite] = useState("");

  /** Merge the guard fields into a payload just before submitting. */
  const withGuard = useCallback(
    (payload) => ({ ...payload, website, elapsedMs: Date.now() - startedAt.current }),
    [website]
  );

  const reset = useCallback(() => {
    startedAt.current = Date.now();
    setWebsite("");
  }, []);

  const field = <HoneypotField value={website} onChange={setWebsite} />;

  return { withGuard, field, reset };
}

export default useFormGuard;
